import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import type {
  CoveragePlanDatesPeriod,
  ModifiedCoveragePlanDatesPeriod,
} from "@/model/coverage-years";
import { setSelectedCoverageYear } from "@/store/active-coverage-slice";
import type { RootState } from "@/store/store";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

type CoverageSelectProps = {
  disableDropdown: boolean;
};

const CoverageSelect: React.FC<CoverageSelectProps> = ({
  disableDropdown = false,
}) => {
  const dispatch = useDispatch();
  const { coveragePlanDates, selectedCoverageYear, activeCoverage } =
    useSelector((state: RootState) => state.planCoverageInfo);
  const [openSelect, setOpenSelect] = useState(false);
  const activeCoveragePlanDates =
    coveragePlanDates.find(
      (item) => item.type === activeCoverage.claimCoverageCode,
    )?.periods ?? [];

  const handleValueChange = (id: string) => {
    const selectedYear = activeCoveragePlanDates.find((item) => item.id === id);

    if (selectedYear) {
      dispatch(setSelectedCoverageYear(selectedYear));
    }
  };

  function getCoveragePeriodRange(
    coveragePlanDate: ModifiedCoveragePlanDatesPeriod,
  ) {
    return `${formatDate(coveragePlanDate?.startDate)} - ${formatDate(coveragePlanDate?.endDate)}`;
  }

  return (
    <div className="flex flex-row items-center justify-center space-x-2">
      <Label>
        {selectedCoverageYear?.isAccumYear
          ? "Summary for Accumulator Year"
          : "Summary for Plan/Accumulator Year"}
        {" : "}
      </Label>
      <Select
        value={
          disableDropdown
            ? activeCoveragePlanDates?.[0]?.id
            : selectedCoverageYear?.id
        }
        onValueChange={handleValueChange}
        open={openSelect}
        onOpenChange={(newOpen) => {
          setOpenSelect(
            !disableDropdown && activeCoveragePlanDates.length > 1
              ? newOpen
              : false,
          );
        }}
      >
        <SelectTrigger
          className={cn(
            "w-[300px]",
            (disableDropdown || activeCoveragePlanDates.length <= 1) &&
              "[&>svg]:hidden",
          )}
        >
          <SelectValue placeholder="Select coverage period">
            {disableDropdown
              ? getCoveragePeriodRange(activeCoveragePlanDates?.[0])
              : selectedCoverageYear
                ? getCoveragePeriodRange(selectedCoverageYear)
                : "Select coverage period"}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {activeCoveragePlanDates.map((item: CoveragePlanDatesPeriod) => {
            const label = `${formatDate(item?.startDate)} - ${formatDate(item?.endDate)}`;
            return (
              <SelectItem
                key={item.id}
                value={item.id}
                className="cursor-pointer"
              >
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CoverageSelect;
